import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';

import url from '../../constants/config';
import { addFilters } from '../store/actions';

import './SavedSearches.scss';

/**
 * Build a short human-readable summary from a filters object.
 * e.g. "Volleyball · Female · 2026 · MH"
 */
const buildSummary = (filters = {}) => {
  const parts = [];
  if (filters.sport) parts.push(filters.sport);
  if (filters.gender) parts.push(filters.gender);
  if (filters.graduationYear && filters.graduationYear !== 'Filter all') {
    parts.push(filters.graduationYear);
  }
  if (filters.position && filters.position !== 'Filter all') {
    parts.push(filters.position);
  }
  return parts.join(' · ') || 'All filters';
};

const SavedSearches = () => {
  const [searches, setSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const fetchSearches = useCallback(async () => {
    try {
      const token = window.localStorage.getItem('authorization');
      if (!token) {
        setIsLoading(false);
        return;
      }
      const { data } = await axios.get(`${url}/coach/saved-searches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSearches(data.savedSearches || []);
    } catch (err) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSearches();
  }, [fetchSearches]);

  const handleViewResults = async search => {
    // Dispatch filters into Redux so FilterBar + Players re-render
    dispatch(addFilters(search.filters));

    // Bump lastUsedAt in background (fire-and-forget)
    const token = window.localStorage.getItem('authorization');
    if (token) {
      axios
        .patch(`${url}/coach/saved-searches/${search._id}/use`, null, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => {}); // silent
    }

    // Navigate to /available-players (the Players component will re-fetch based on Redux)
    if (window.location.pathname !== '/available-players') {
      history.push('/available-players');
    }
  };

  const handleDelete = async (e, searchId) => {
    e.stopPropagation();
    try {
      const token = window.localStorage.getItem('authorization');
      await axios.delete(`${url}/coach/saved-searches/${searchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSearches(prev => prev.filter(s => s._id !== searchId));
    } catch (err) {
      // Silently fail – the search stays in the list
    }
  };

  // ── Render states ────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="ss">
        <div className="ss__header">
          <h3 className="ss__title">
            <i className="fas fa-bookmark" /> Saved Searches
          </h3>
        </div>
        <div className="ss__loading">
          <div className="ss__loading-dot" />
          <div className="ss__loading-dot" />
          <div className="ss__loading-dot" />
        </div>
      </div>
    );
  }

  if (error) {
    return null; // Silent fallback
  }

  if (!searches.length) {
    return (
      <div className="ss">
        <div className="ss__header">
          <h3 className="ss__title">
            <i className="fas fa-bookmark" /> Saved Searches
          </h3>
        </div>
        <div className="ss__empty">
          <i className="far fa-bookmark" />
          <span>No saved searches yet. Use "Save this search" above.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ss">
      <div className="ss__header">
        <h3 className="ss__title">
          <i className="fas fa-bookmark" /> Saved Searches
        </h3>
        <span className="ss__count">
          {searches.length} search{searches.length !== 1 ? 'es' : ''}
        </span>
      </div>

      <div className="ss__list">
        {searches.map(search => (
          <div
            key={search._id}
            className="ss__item"
            onClick={() => handleViewResults(search)}
          >
            <div className="ss__item-info">
              <span className="ss__item-name">{search.name}</span>
              <span className="ss__item-summary">
                {buildSummary(search.filters)}
              </span>
            </div>
            <div className="ss__item-actions">
              <button
                type="button"
                className="ss__item-btn ss__item-btn--view"
                onClick={() => handleViewResults(search)}
                title="View results"
              >
                <i className="fas fa-search" /> View
              </button>
              <button
                type="button"
                className="ss__item-btn ss__item-btn--delete"
                onClick={e => handleDelete(e, search._id)}
                title="Delete saved search"
              >
                <i className="fas fa-trash-alt" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedSearches;
